import { VideoService } from './VideoService'

interface VideoSimulation {
  id: string
  title: string
  description: string
  videoPath: string
  lop: number
  chuong: number
  bai: number
  chuongName: string
  baiName: string
  difficulty: 'dễ' | 'trung bình' | 'khó'
  duration: number
  rating: number
  tags: string[]
  type: 'mo-phong'
  url: string
}

class VideoSimulationService {
  // Mapping độ khó dựa trên lớp và chương
  private static getDifficulty(lop: number, chuong: number, bai: number): 'dễ' | 'trung bình' | 'khó' {
    // Lớp 10: chủ yếu dễ và trung bình
    if (lop === 10) {
      if (chuong <= 1) return 'dễ'
      if (chuong <= 3) return 'trung bình'
      return 'khó'
    }

    // Lớp 11: chủ yếu trung bình và khó
    if (lop === 11) {
      if (chuong <= 2) return 'trung bình'
      return 'khó'
    }

    // Lớp 12: chủ yếu khó
    if (lop === 12) {
      if (chuong <= 1) return 'trung bình'
      return 'khó'
    }

    return 'trung bình'
  }

  // Mapping thời lượng dựa trên độ khó
  private static getDuration(difficulty: 'dễ' | 'trung bình' | 'khó'): number {
    switch (difficulty) {
      case 'dễ': return 15 + Math.floor(Math.random() * 10) // 15-25 phút
      case 'trung bình': return 20 + Math.floor(Math.random() * 15) // 20-35 phút
      case 'khó': return 25 + Math.floor(Math.random() * 20) // 25-45 phút
      default: return 20
    }
  }

  // Mapping rating dựa trên độ khó
  private static getRating(difficulty: 'dễ' | 'trung bình' | 'khó'): number {
    switch (difficulty) {
      case 'dễ': return 4.2 + Math.random() * 0.6 // 4.2-4.8
      case 'trung bình': return 4.4 + Math.random() * 0.4 // 4.4-4.8
      case 'khó': return 4.6 + Math.random() * 0.3 // 4.6-4.9
      default: return 4.5
    }
  }

  // Tạo tags dựa trên tên chương và bài
  private static generateTags(chuongName: string, baiName: string): string[] {
    const tags = ['mô phỏng', 'video']

    // Tags từ tên chương
    if (chuongName.includes('dao-dong')) tags.push('dao động')
    if (chuongName.includes('song')) tags.push('sóng')
    if (chuongName.includes('dien-truong')) tags.push('điện trường')
    if (chuongName.includes('dong-dien')) tags.push('dòng điện')
    if (chuongName.includes('dong-hoc')) tags.push('động học')
    if (chuongName.includes('dong-luc-hoc')) tags.push('động lực học')
    if (chuongName.includes('nang-luong')) tags.push('năng lượng')
    if (chuongName.includes('chuyen-dong-tron')) tags.push('chuyển động tròn')
    if (chuongName.includes('bien-dang')) tags.push('biến dạng')
    if (chuongName.includes('vat-li-nhiet')) tags.push('vật lý nhiệt')
    if (chuongName.includes('khi-li-tuong')) tags.push('khí lý tưởng')
    if (chuongName.includes('tu-truong')) tags.push('từ trường')
    if (chuongName.includes('hat-nhan')) tags.push('hạt nhân')

    // Tags từ tên bài
    if (baiName.includes('dao-dong-dieu-hoa')) tags.push('dao động điều hòa')
    if (baiName.includes('con-lac')) tags.push('con lắc')
    if (baiName.includes('giao-thoa')) tags.push('giao thoa')
    if (baiName.includes('song-dung')) tags.push('sóng dừng')
    if (baiName.includes('coulomb')) tags.push('Coulomb')
    if (baiName.includes('dien-the')) tags.push('điện thế')
    if (baiName.includes('tu-dien')) tags.push('tụ điện')
    if (baiName.includes('newton')) tags.push('Newton')
    if (baiName.includes('nang-luong')) tags.push('năng lượng')
    if (baiName.includes('cong')) tags.push('công')
    if (baiName.includes('dong-nang')) tags.push('động năng')
    if (baiName.includes('the-nang')) tags.push('thế năng')

    return [...new Set(tags)] // Remove duplicates
  }

  // Parse tên từ đường dẫn
  private static parsePath(videoPath: string): {
    lop: number
    chuong: number
    bai: number
    chuongName: string
    baiName: string
  } {
    // Pattern: /videos/lop-11/chuong-1-dao-dong/bai-1-dao-dong-dieu-hoa-phuong-trinh-dao-dong-dieu-hoa.mp4
    const parts = videoPath.split('/')

    // Extract lop
    const lopMatch = parts.find(p => p.startsWith('lop-'))?.match(/lop-(\d+)/)
    const lop = lopMatch ? parseInt(lopMatch[1]) : 0 // Default to 0 instead of 10 to detect failure

    // Extract chuong
    const chuongMatch = parts.find(p => p.startsWith('chuong-'))?.match(/chuong-(\d+)/)
    const chuong = chuongMatch ? parseInt(chuongMatch[1]) : 1

    // Extract bai
    const baiMatch = parts.find(p => p.startsWith('bai-'))?.match(/bai-(\d+)/)
    const bai = baiMatch ? parseInt(baiMatch[1]) : 1

    // Extract chuong name
    const chuongPart = parts.find(p => p.startsWith('chuong-'))
    const chuongName = chuongPart ? chuongPart.replace(/^chuong-\d+-/, '') : ''

    // Extract bai name
    const baiPart = parts.find(p => p.startsWith('bai-'))
    const baiName = baiPart ? baiPart.replace(/^bai-\d+-/, '').replace(/\.mp4$/, '') : ''

    return { lop, chuong, bai, chuongName, baiName }
  }

  // Tạo title từ tên bài
  private static generateTitle(baiName: string, chuongName: string): string {
    if (!baiName) return 'Mô phỏng AI-PhysicsBook'

    // Convert từ kebab-case sang title case
    const title = baiName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

    return `Mô phỏng ${title}`
  }

  // Tạo description từ title và tags
  private static generateDescription(title: string, tags: string[]): string {
    const mainTags = tags.filter(tag => tag !== 'mô phỏng' && tag !== 'video').slice(0, 3)
    return `Video ${title.toLowerCase()} - ${mainTags.join(', ')}`
  }

  // Tạo tên chương theo lớp
  private static getChuongName(lop: number, chuong: number): string {
    if (lop === 10) {
      const chuongNames: { [key: number]: string } = {
        1: 'Động học chất điểm',
        2: 'Động lực học chất điểm',
        3: 'Cân bằng và chuyển động của vật rắn',
        4: 'Các định luật bảo toàn'
      }
      return chuongNames[chuong] || `Chương ${chuong}`
    } else if (lop === 11) {
      const chuongNames: { [key: number]: string } = {
        1: 'Dao động cơ',
        2: 'Sóng cơ và sóng âm',
        3: 'Dòng điện xoay chiều',
        4: 'Dao động và sóng điện từ'
      }
      return chuongNames[chuong] || `Chương ${chuong}`
    } else if (lop === 12) {
      const chuongNames: { [key: number]: string } = {
        1: 'Điện tích, điện trường',
        2: 'Dòng điện không đổi',
        3: 'Từ trường',
        4: 'Vật lí hạt nhân'
      }
      return chuongNames[chuong] || `Chương ${chuong}`
    }
    return `Chương ${chuong}`
  }

  // Tạo tên bài học
  private static getBaiName(lop: number, chuong: number, bai: number): string {
    // Có thể mở rộng để tạo tên bài học cụ thể theo lớp, chương, bài
    return `Bài ${bai}`
  }

  // Kiểm tra video có tồn tại không
  static async checkVideoExists(videoPath: string): Promise<boolean> {
    try {
      // Trong môi trường development/production, video trong public/videos được serve dưới đường dẫn /videos/...
      console.log(`🔍 Checking video: ${videoPath}`)
      const response = await fetch(videoPath, { method: 'HEAD' })
      const exists = response.ok
      console.log(`${exists ? '✅' : '❌'} Video ${exists ? 'exists' : 'not found'}: ${videoPath}`)
      return exists
    } catch (error) {
      console.warn(`Failed to check video: ${videoPath}`, error)
      return false
    }
  }


  // Scan thư mục videos và tạo danh sách mô phỏng
  static async getVideoSimulations(): Promise<VideoSimulation[]> {
    console.log('🎬 VideoSimulationService: Loading video simulations using VideoService')

    const simulations: VideoSimulation[] = []

    // Sử dụng VideoService để lấy danh sách video có sẵn
    const availableVideos = VideoService.getAllAvailableVideos()

    console.log(`🔍 Processing ${availableVideos.length} available videos...`)

    for (const video of availableVideos) {
      try {
        console.log(`🔍 Processing video: ${video.videoPath}`)

        // Parse path để lấy chuongName và baiName ở dạng kebab-case
        const parsedPath = this.parsePath(video.videoPath)

        // Tạo metadata cho video
        const chuongName = this.getChuongName(video.lop, video.chuong)
        const baiName = this.getBaiName(video.lop, video.chuong, video.bai)
        const difficulty = this.getDifficulty(video.lop, video.chuong, video.bai)
        const duration = this.getDuration(difficulty)
        const rating = this.getRating(difficulty)

        // Sử dụng chuongName và baiName từ path (kebab-case) để generate tags
        const tags = this.generateTags(parsedPath.chuongName, parsedPath.baiName)
        const title = `Mô phỏng ${video.title}`
        const description = this.generateDescription(title, tags)

        const simulation: VideoSimulation = {
          id: video.id,
          title,
          description,
          videoPath: video.videoPath,
          lop: video.lop,
          chuong: video.chuong,
          bai: video.bai,
          chuongName,
          baiName,
          difficulty,
          duration,
          rating: Math.round(rating * 10) / 10,
          tags,
          type: 'mo-phong',
          url: `/lop/${video.lop}/chuong/${video.chuong}/bai/${video.bai}?section=moPhong`
        }

        simulations.push(simulation)
        console.log(`✅ Added simulation: ${simulation.title}`)
      } catch (error) {
        console.warn(`Failed to process video ${video.videoPath}:`, error)
      }
    }

    console.log(`🎬 Total video simulations found: ${simulations.length}`)
    console.log('🎬 Final simulations array:', simulations)
    return simulations
  }

  // Lọc mô phỏng theo điều kiện
  static filterSimulations(
    simulations: VideoSimulation[],
    filters: {
      lop?: number
      searchTerm?: string
    }
  ): VideoSimulation[] {
    let filtered = [...simulations]

    if (filters.lop) {
      filtered = filtered.filter(sim => sim.lop === filters.lop)
    }

    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase()
      filtered = filtered.filter(sim =>
        sim.title.toLowerCase().includes(searchLower) ||
        sim.description.toLowerCase().includes(searchLower) ||
        sim.tags.some(tag => tag.toLowerCase().includes(searchLower))
      )
    }

    return filtered
  }
}

export default VideoSimulationService
export type { VideoSimulation }